// src/api/sftfClient.js
import axios from "axios";
import { useCallback } from "react";
import { getCurrentEnvironment, ENVIRONMENT_CONFIG } from "../config/environment";

// Configurar axios para manejar mejor las respuestas
// No configurar headers por defecto aquí para evitar conflictos
// Cada petición especificará sus propios headers

// Configurar transformResponse para manejar respuestas que no son JSON válido
axios.defaults.transformResponse = [
  function (data, headers) {
    // Si data es string, intentar parsearlo como JSON
    if (typeof data === 'string') {
      // Si está vacío, devolver objeto vacío
      if (!data || data.trim() === '') {
        return {};
      }
      
      // Si el Content-Type no es JSON, devolver el string tal cual
      const contentType = headers && headers['content-type'];
      if (contentType && !contentType.includes('application/json') && !contentType.includes('text/json')) {
        // Es HTML o texto plano, devolver como string para que el interceptor lo maneje
        return data;
      }
      
      // Intentar parsear como JSON
      try {
        const parsed = JSON.parse(data);
        return parsed;
      } catch (e) {
        // Si no es JSON válido, verificar si es un error conocido
        const trimmed = data.trim();
        if (trimmed.startsWith('ERROR:') || trimmed.includes('Not Found') || trimmed.startsWith('<')) {
          // Es HTML o mensaje de error, devolver como string
          return data;
        }
        // Intentar devolver como objeto con el mensaje
        return { message: trimmed, raw: data };
      }
    }
    return data;
  }
];

// Interceptor para manejar respuestas que no son JSON
axios.interceptors.response.use(
  (response) => {
    // Verificar si la respuesta es texto plano o HTML en lugar de JSON
    if (typeof response.data === 'string') {
      const errorMessage = response.data.trim();
      
      // Si contiene "ERROR:" o "Not Found" o es HTML, convertir en error
      if (errorMessage.startsWith('ERROR:') || 
          errorMessage.includes('Not Found') || 
          errorMessage.startsWith('<') ||
          errorMessage.toLowerCase().includes('<!doctype') ||
          errorMessage.toLowerCase().includes('<html')) {
        return Promise.reject({
          response: {
            status: response.status || 404,
            statusText: response.statusText || 'Not Found',
            data: {
              message: errorMessage.includes('Not Found') ? 'Not Found' : errorMessage,
              Success: false,
              Message: errorMessage.includes('Not Found') ? 'Not Found' : errorMessage
            },
            headers: response.headers
          }
        });
      }
      
      // Si es otro tipo de texto, convertirlo a objeto
      response.data = {
        message: errorMessage,
        Success: false,
        Message: errorMessage
      };
    }
    return response;
  },
  (error) => {
    // Si hay un error y la respuesta no es JSON, intentar parsearla
    if (error.response) {
      const data = error.response.data;
      
      // Si la respuesta es string, crear un objeto de error estructurado
      if (typeof data === 'string') {
        const errorMessage = data.trim();
        const isHtml = errorMessage.startsWith('<') || 
                      errorMessage.toLowerCase().includes('<!doctype') ||
                      errorMessage.toLowerCase().includes('<html');
        
        error.response.data = {
          message: isHtml ? 'Not Found' : (errorMessage || `Error ${error.response.status}: ${error.response.statusText || 'Not Found'}`),
          status: error.response.status,
          Success: false,
          Message: isHtml ? 'Not Found' : (errorMessage || error.response.statusText || 'Not Found'),
          raw: isHtml ? undefined : errorMessage
        };
      }
    }
    return Promise.reject(error);
  }
);

// Configuración de ambiente - Se obtiene dinámicamente
// const getEnvironment = () => process.env.REACT_APP_ENVIRONMENT || 'PROD';

/**
 * Construye las URLs completas de un ambiente a partir de su configuración.
 * environment.js es la única fuente de rutas: tener dos listas de URLs fue lo
 * que dejó a la app apuntando a un endpoint que ya no existía.
 *
 * @param {'PROD'|'PREPROD'} claveAmbiente
 * @returns {Object} Mapa de nombre de endpoint a URL completa.
 */
const construirUrlsDeAmbiente = (claveAmbiente) => {
  const { baseUrl, endpoints } = ENVIRONMENT_CONFIG[claveAmbiente];
  return Object.fromEntries(
    Object.entries(endpoints).map(([nombre, ruta]) => [nombre, `${baseUrl}/${ruta}`])
  );
};

const BASE_URL_PROD = construirUrlsDeAmbiente('PROD');
const BASE_URL_PREPROD = construirUrlsDeAmbiente('PREPROD');

// Valores hardcodeados como fallback para catálogos
const FALLBACK_ESTATUS_FACTURA = [
  {
    "ClaveEstatusFactura": 0,
    "EstatusFactura": "Por procesar"
  },
  {
    "ClaveEstatusFactura": 1,
    "EstatusFactura": "Procesada"
  },
  {
    "ClaveEstatusFactura": 2,
    "EstatusFactura": "Por reprocesar"
  },
  {
    "ClaveEstatusFactura": 3,
    "EstatusFactura": "Reprocesada"
  },
  {
    "ClaveEstatusFactura": 4,
    "EstatusFactura": "Cancelada"
  }
];

const FALLBACK_TIPOS_FACTURA = [
  {
    "ClaveTipoFactura": 1,
    "TipoFactura": "I",
    "Descripcion": "Arrastre"
  },
  {
    "ClaveTipoFactura": 2,
    "TipoFactura": "I",
    "Descripcion": "Notas Cargo"
  },
  {
    "ClaveTipoFactura": 3,
    "TipoFactura": "E",
    "Descripcion": "Notas Crédito"
  },
  {
    "ClaveTipoFactura": 4,
    "TipoFactura": "I",
    "Descripcion": "Otros"
  }
];

// Selección automática del ambiente
const getBaseUrl = () => {
  const env = getCurrentEnvironment();
  return env.isPreprod ? BASE_URL_PREPROD : BASE_URL_PROD;
};

// Mensaje amigable cuando el hub de Cryoinfra no responde tras varios reintentos.
export const MENSAJE_SERVICIO_NO_DISPONIBLE =
  "El servicio de Cryoinfra no está disponible en este momento. Intenta de nuevo en unos minutos.";

/**
 * Detecta los errores intermitentes conocidos del hub de Cryoinfra que conviene
 * reintentar en lugar de mostrarlos al usuario. Acepta un string o un objeto
 * (respuesta o error) y busca las firmas conocidas del hub.
 * @param {string|object|null|undefined} payload
 * @returns {boolean}
 */
export const isErrorTransitorioHub = (payload) => {
  if (payload == null) return false;
  let texto;
  if (typeof payload === "string") {
    texto = payload;
  } else {
    texto = payload.message || payload.Message || payload.data || payload.raw || "";
  }
  if (typeof texto !== "string") return false;
  const t = texto.toLowerCase();
  return (
    t.includes("no se encuentra en l") || // "no se encuentra en línea" / "linea"
    t.includes("time out after") ||
    t.includes("error: bad request")
  );
};

/**
 * Normaliza los registros de bitácora que devuelve el hub.
 *
 * La API responde en PascalCase, pero `extraerClaveBitacora` en addendaXml.js
 * y el render del autocompletado (BitacoraAutocomplete.js) esperan las claves
 * en minúsculas. Se agregan los alias sin quitar las originales, para no
 * tocar la generación de la Addenda.
 *
 * Una respuesta de 0 bytes llega aquí como `{}` (por transformResponse), y
 * cualquier texto de error llega como string: ambos casos devuelven [].
 *
 * @param {unknown} data
 * @returns {Array<Object>}
 */
export const normalizarBitacoras = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map((registro) => ({
    ...registro,
    claveBitacora: registro.ClaveBitacora,
    serieBitacora: registro.SerieBitacora ?? "",
    folioBitacora: registro.FolioBitacora,
    origenNombre: registro.OrigenNombre ?? "",
    destinoNombre: registro.DestinoNombre ?? "",
    operadorNombre: registro.OperadorNombre ?? "",
  }));
};

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Realiza un POST de consulta al hub con reintento automático ante los errores
 * transitorios de Cryoinfra ("no se encuentra en línea", "Time out after...",
 * "ERROR: Bad Request") y ante timeouts del cliente.
 *
 * - Reintenta hasta `maxIntentos` con el backoff indicado.
 * - Corta cada intento a `timeoutMs` para no esperar el timeout de 100s del hub.
 * - Los errores NO transitorios (p.ej. credenciales) se propagan sin reintentar.
 * - Al agotar los intentos lanza `{ message: MENSAJE_SERVICIO_NO_DISPONIBLE, transitorio: true }`.
 *
 * @param {string} url
 * @param {object} body
 * @param {{ maxIntentos?: number, timeoutMs?: number, backoffMs?: number[], onRetry?: Function }} [opts]
 */
export const postConsultaConReintento = async (
  url,
  body,
  { maxIntentos = 3, timeoutMs = 45000, backoffMs = [0, 1500, 3000], onRetry } = {}
) => {
  for (let intento = 1; intento <= maxIntentos; intento++) {
    try {
      const response = await axios.post(url, body, {
        timeout: timeoutMs,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      if (!isErrorTransitorioHub(response.data)) {
        return response.data;
      }
      // Respuesta 200 con una firma de error transitorio del hub: reintentar.
    } catch (error) {
      const esTimeoutCliente =
        error?.code === "ECONNABORTED" ||
        (typeof error?.message === "string" &&
          error.message.toLowerCase().includes("timeout"));
      const transitorio = esTimeoutCliente || isErrorTransitorioHub(error?.response?.data);
      if (!transitorio) {
        // Error real (credenciales, red, etc.): propagar sin reintentar.
        throw error;
      }
    }

    // Hubo un fallo transitorio; si quedan intentos, esperar el backoff y reintentar.
    if (intento < maxIntentos) {
      if (typeof onRetry === "function") onRetry({ intento: intento + 1, maxIntentos });
      await esperar(backoffMs[intento] ?? 0);
    }
  }

  const errorTransitorio = new Error(MENSAJE_SERVICIO_NO_DISPONIBLE);
  errorTransitorio.transitorio = true;
  throw errorTransitorio;
};

/**
 * Hook personalizado para consumir los endpoints SFTF
 * @param {{ user: string, pass: string }} auth
 */
export const useSftfApi = (auth) => {
  /**
   * Realiza una consulta con filtros como query params.
   * @param {string} queryParams - Ejemplo: "SerieBitacora=DQ&FolioBitacora=12345"
   */
  const postConsulta = useCallback(async (queryParams = "", opts = {}) => {
    try {
      const baseUrl = getBaseUrl();
      const url = queryParams ? `${baseUrl.consulta}?${queryParams}` : baseUrl.consulta;
      return await postConsultaConReintento(url, auth, opts);
    } catch (error) {
      if (error?.transitorio) throw error; // mensaje amigable ya formado
      throw error.response?.data || { message: "Error de red en consulta" };
    }
  }, [auth]);

  /**
   * Realiza una actualización de registros de factura.
   * @param {Array} facturas - Lista de objetos con todos los datos requeridos.
   */

  
  const postActualiza = useCallback(async (facturas = []) => {
    try {
      const payload = {
        user: auth.user,
        pass: auth.pass,
        facturas: facturas
      };
      const baseUrl = getBaseUrl();
      const env = getCurrentEnvironment();
      console.log("Environment:", env.name);
      console.log("URL:", baseUrl.actualiza);
      console.log("payload", payload);
      
      // Configurar headers exactamente como Postman
      // Solo Content-Type, sin otros headers automáticos que puedan interferir
      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        maxBodyLength: Infinity, // Permitir payloads de cualquier tamaño (como en Postman)
        maxContentLength: Infinity, // Permitir respuestas de cualquier tamaño
        // No usar responseType: 'text' - dejar que axios maneje JSON normalmente
        // El transformResponse ya está configurado para manejar casos edge
        // Asegurar que no se envíen headers adicionales
        validateStatus: function (status) {
          // Aceptar todos los códigos de estado para manejar respuestas con success: false
          return status >= 200 && status < 500;
        }
      };
      
      console.log("Request config:", config);
      console.log("Payload stringified:", JSON.stringify(payload));
      console.log("Full URL:", baseUrl.actualiza);
      
      const response = await axios.post(baseUrl.actualiza, payload, config);
      
      console.log("Response status:", response.status);
      console.log("Response headers:", JSON.stringify(response.headers, null, 2));
      console.log("Response data type:", typeof response.data);
      console.log("Response data:", response.data);
      
      // Si la respuesta es string, loguear los primeros caracteres
      if (typeof response.data === 'string') {
        console.log("Response is string, first 500 chars:", response.data.substring(0, 500));
      }
      
      // La respuesta debería ser JSON válido (como en Postman)
      // Si success es false, lanzar error para que se maneje en el catch
      if (response.data && response.data.success === false) {
        throw {
          response: {
            status: response.status,
            statusText: response.statusText,
            data: response.data
          }
        };
      }
      
      return response.data;
    } catch (error) {
      console.error("=== ERROR CAPTURED ===");
      console.error("Error type:", typeof error);
      console.error("Error keys:", Object.keys(error));
      
      // Manejar diferentes tipos de errores
      if (error.response) {
        // El servidor respondió (puede ser 200 con success: false o un error HTTP)
        const status = error.response.status;
        const data = error.response.data;
        
        console.error("Response status:", status);
        console.error("Response statusText:", error.response.statusText);
        console.error("Response data type:", typeof data);
        console.error("Response data:", data);
        
        // Si data es string, loguear los primeros caracteres
        if (typeof data === 'string') {
          console.error("Response data (string, first 500 chars):", data.substring(0, 500));
        }
        
        // Si la respuesta tiene el formato de Postman (success: false con errors)
        if (data && typeof data === 'object' && data.success === false) {
          // Construir mensaje de error desde el formato de respuesta
          let errorMessage = data.message || 'Error al procesar la solicitud';
          
          // Agregar los errores específicos si existen
          if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
            errorMessage += '\n' + data.errors.join('\n');
          }
          
          throw {
            message: errorMessage,
            status: status,
            data: data,
            errors: data.errors || []
          };
        }
        
      // Si la respuesta no es JSON (puede ser HTML o texto plano)
      if (typeof data === 'string' || !data) {
        const errorMessage = typeof data === 'string' ? data.trim() : '';
        const isHtml = errorMessage.startsWith('<') || 
                      errorMessage.toLowerCase().includes('<!doctype') ||
                      errorMessage.toLowerCase().includes('<html');
        
        console.error(`Error ${status}:`, error.response.statusText);
        console.error("Response data (raw):", errorMessage.substring(0, 200));
        
        throw {
          message: isHtml ? 'Not Found' : (errorMessage || `Error ${status}: ${error.response.statusText || 'Not Found'}`),
          status: status,
          data: data
        };
      }
        
        // Si es un objeto JSON con otro formato
        throw {
          message: data.message || data.Message || `Error ${status}: ${error.response.statusText || 'Error en la petición'}`,
          status: status,
          data: data
        };
      } else if (error.request) {
        // La petición se hizo pero no se recibió respuesta
        console.error("No se recibió respuesta del servidor:", error.request);
        throw {
          message: "No se pudo conectar con el servidor. Verifica tu conexión a internet.",
          request: error.request
        };
      } else {
        // Algo pasó al configurar la petición
        console.error("Error al configurar la petición:", error.message);
        throw {
          message: error.message || "Error desconocido al realizar la petición"
        };
      }
    }
  }, [auth]);

  /**
   * Consulta el catálogo de tipos de factura.
   * Ante cualquier fallo del hub devuelve los valores de respaldo para no dejar
   * la pantalla sin opciones.
   *
   * Un solo intento con corte a 10 s: ya existe un respaldo verificado como
   * idéntico al catálogo real, así que no vale la pena bloquear el formulario
   * de facturación hasta ~93 s (3 intentos, corte a 45 s) esperando un hub
   * caído para terminar mostrando de todos modos ese mismo respaldo.
   * @param {string} queryParams - Parámetros de consulta opcionales
   */
  const consultarTiposFactura = useCallback(async (queryParams = "claveEstatus=1") => {
    try {
      const baseUrl = getBaseUrl();
      const url = queryParams ? `${baseUrl.tiposFactura}?${queryParams}` : baseUrl.tiposFactura;
      const data = await postConsultaConReintento(url, auth, { maxIntentos: 1, timeoutMs: 10000 });
      return Array.isArray(data) && data.length > 0 ? data : FALLBACK_TIPOS_FACTURA;
    } catch (error) {
      console.warn("Tipos de factura: el servicio no respondió, se usan valores de respaldo:", error.message);
      return FALLBACK_TIPOS_FACTURA;
    }
  }, [auth]);

  /**
   * Consulta el catálogo de estatus de factura.
   * Ante cualquier fallo del hub devuelve los valores de respaldo.
   *
   * Mismo criterio que consultarTiposFactura: un solo intento con corte a
   * 10 s en vez de reintentar hasta ~93 s contra un catálogo que ya tiene
   * respaldo verificado.
   * @param {string} queryParams - Parámetros de consulta opcionales
   */
  const consultarEstatusFactura = useCallback(async (queryParams = "claveEstatus=1") => {
    try {
      const baseUrl = getBaseUrl();
      const url = queryParams ? `${baseUrl.estatusFactura}?${queryParams}` : baseUrl.estatusFactura;
      const data = await postConsultaConReintento(url, auth, { maxIntentos: 1, timeoutMs: 10000 });
      return Array.isArray(data) && data.length > 0 ? data : FALLBACK_ESTATUS_FACTURA;
    } catch (error) {
      console.warn("Estatus de factura: el servicio no respondió, se usan valores de respaldo:", error.message);
      return FALLBACK_ESTATUS_FACTURA;
    }
  }, [auth]);

  /**
   * Consulta el catálogo de conceptos de gasto.
   * @param {string} queryParams - Parámetros de consulta opcionales
   */
  const consultarConceptosGasto = useCallback(async (queryParams = "estatus=1") => {
    try {
      const baseUrl = getBaseUrl();
      const url = queryParams ? `${baseUrl.conceptosGasto}?${queryParams}` : baseUrl.conceptosGasto;
      return await postConsultaConReintento(url, auth);
    } catch (error) {
      if (error?.transitorio) throw error;
      throw error.response?.data || { message: "Error de red en consulta de conceptos de gasto" };
    }
  }, [auth]);

  /**
   * Consulta conceptos de gasto por clave específica.
   * @param {number} claveConcepto - Clave del concepto a buscar
   */
  const consultarConceptoPorClave = useCallback(async (claveConcepto) => {
    try {
      const baseUrl = getBaseUrl();
      const url = `${baseUrl.conceptosGasto}?claveConcepto=${claveConcepto}`;
      return await postConsultaConReintento(url, auth);
    } catch (error) {
      if (error?.transitorio) throw error;
      throw error.response?.data || { message: "Error de red en consulta de concepto por clave" };
    }
  }, [auth]);

  /**
   * Consulta conceptos de gasto por clave de producto.
   * @param {number} claveProducto - Clave del producto a buscar
   */
  const consultarConceptosPorProducto = useCallback(async (claveProducto) => {
    try {
      const baseUrl = getBaseUrl();
      const url = `${baseUrl.conceptosGasto}?claveProducto=${claveProducto}`;
      return await postConsultaConReintento(url, auth);
    } catch (error) {
      if (error?.transitorio) throw error;
      throw error.response?.data || { message: "Error de red en consulta de conceptos por producto" };
    }
  }, [auth]);

  /**
   * Consulta conceptos de gasto por tipo de unidad.
   * @param {number} claveTipoUnidad - Clave del tipo de unidad a buscar
   */
  const consultarConceptosPorTipoUnidad = useCallback(async (claveTipoUnidad) => {
    try {
      const baseUrl = getBaseUrl();
      const url = `${baseUrl.conceptosGasto}?claveTipoUnidad=${claveTipoUnidad}`;
      return await postConsultaConReintento(url, auth);
    } catch (error) {
      if (error?.transitorio) throw error;
      throw error.response?.data || { message: "Error de red en consulta de conceptos por tipo de unidad" };
    }
  }, [auth]);

  /**
   * Busca una bitácora por folio, para el autocompletado.
   *
   * El hub solo responde la consulta de bitácoras filtrando por folio: cualquier
   * listado (por serie, por estatus, o sin filtro) truena con Bad Request o
   * timeout. Por eso esto es una búsqueda por folio y no un filtrado en cliente.
   *
   * @param {string} searchText - Folio de la bitácora.
   * @returns {Promise<Array>} Registros normalizados, o [] si no hay folio válido.
   */
  const buscarBitacoras = useCallback(async (searchText) => {
    const folio = String(searchText ?? "").trim();
    if (!/^\d+$/.test(folio)) return [];

    const baseUrl = getBaseUrl();
    const url = `${baseUrl.consulta}?FolioBitacora=${encodeURIComponent(folio)}&ConTimbre=false`;
    const data = await postConsultaConReintento(url, auth);
    return normalizarBitacoras(data);
  }, [auth]);

  /**
   * Consulta las facturas ya timbradas (ConTimbre=true).
   *
   * Es la única consulta de FacturaFletera que hoy responde en bloque: devuelve
   * el histórico completo del RFC en ~2.5 s. Los filtros aceptados junto a
   * ConTimbre=true son Serie y Folio de bitácora.
   *
   * @param {{ SerieBitacora?: string, FolioBitacora?: string|number }} filtros
   * @param {Object} opts Opciones de reintento (p.ej. onRetry).
   * @returns {Promise<Array>}
   */
  const consultarFacturasTimbradas = useCallback(async (filtros = {}, opts = {}) => {
    const baseUrl = getBaseUrl();
    const params = ['ConTimbre=true'];

    ['SerieBitacora', 'FolioBitacora'].forEach((campo) => {
      const valor = filtros?.[campo];
      if (valor !== undefined && valor !== null && String(valor).trim() !== '') {
        params.push(`${campo}=${encodeURIComponent(String(valor).trim())}`);
      }
    });

    const url = `${baseUrl.consulta}?${params.join('&')}`;
    const data = await postConsultaConReintento(url, auth, { timeoutMs: 60000, ...opts });
    return Array.isArray(data) ? data : [];
  }, [auth]);

  /**
   * Busca conceptos de gasto por texto (para autocompletado).
   * @param {string} searchText - Texto a buscar en la descripción del concepto
   */
  const buscarConceptos = useCallback(async (searchText) => {
    try {
      const baseUrl = getBaseUrl();
      const url = `${baseUrl.conceptosGasto}?estatus=1`;
      const data = await postConsultaConReintento(url, auth);

      // Filtrar los resultados por el texto de búsqueda
      if (data && Array.isArray(data)) {
        const filtered = data.filter(concepto =>
          concepto.Descripcion?.toLowerCase().includes(searchText.toLowerCase()) ||
          concepto.CodigoGasto?.toString().includes(searchText) ||
          concepto.ClaveConcepto?.toString().includes(searchText) ||
          concepto.Producto?.toLowerCase().includes(searchText.toLowerCase()) ||
          concepto.TipoUnidad?.toLowerCase().includes(searchText.toLowerCase())
        );
        return filtered;
      }

      console.warn("Response data is not an array:", data);
      return [];
    } catch (error) {
      console.warn("Error al consultar conceptos de gasto desde API:", error.message);
      if (error?.transitorio) throw error;
      throw error.response?.data || { message: "Error de red en consulta de conceptos de gasto" };
    }
  }, [auth]);

  return {
    postConsulta,
    postActualiza,
    consultarTiposFactura,
    consultarEstatusFactura,
    consultarConceptosGasto,
    consultarConceptoPorClave,
    consultarConceptosPorProducto,
    consultarConceptosPorTipoUnidad,
    consultarFacturasTimbradas,
    buscarConceptos,
    buscarBitacoras
  };

};

// Exportar información del ambiente para debugging
export const getEnvironmentInfo = () => {
  const env = getCurrentEnvironment();
  const baseUrl = getBaseUrl();
  return {
    current: env.key,
    isProd: env.isProd,
    isPreprod: env.isPreprod,
    baseUrl: baseUrl
  };
};

// Exportar las URLs para referencia
export { BASE_URL_PROD, BASE_URL_PREPROD, getBaseUrl };
